import type { IxdDlView, PeriodLabels } from "@/apis/compare/types";

/** 연간 보고서 IXD는 기말 표든 기간 표든 항상 이 라벨이라 '말' 여부를 읽을 수 없다. */
const ANNUAL_PERIOD_LABELS: ReadonlySet<string> = new Set([
  "당기",
  "전기",
  "전전기",
]);

/**
 * IXD 편집기 비교표시설정 › 비교표시유형의 선택지 문구. 사용자가 편집기에서 고를 이름
 * 그대로라야 바로 찾는다 — 편집기 문구가 바뀌면 여기만 고친다. 분기·반기 보고서에만 있다
 * (사업보고서는 편집기가 이 선택을 비활성화한다).
 */
const COMPARE_DISPLAY_TYPE_BY_PERIOD_END = {
  true: "당분(반)기말,전기말",
  false: "당분(반)기,전분(반)기",
} as const;

/**
 * IXD 표가 기말 시점 표인지 기간 라벨로 추측한다 — 라벨 하나라도 '말'이 있으면 true.
 * 라벨이 없거나 전부 연간 라벨이면 null(모름).
 *
 * ponytail: dlView.isPeriodEnd 를 아직 안 주는 백엔드용. 배포가 확인되면 이 함수와
 * ANNUAL_PERIOD_LABELS 를 지운다 — 값이 null 인 표는 라벨도 비어 있거나 사업보고서라
 * 추측해도 결과가 같다(백엔드 확인).
 */
const guessIxdIsPeriodEndFromLabels = (
  periodLabels: PeriodLabels | null | undefined,
): boolean | null => {
  const labels = Object.values(periodLabels ?? {}).flatMap((label) => {
    const trimmed = label?.trim();
    return trimmed ? [trimmed] : [];
  });
  if (labels.length === 0) return null;
  if (labels.some((label) => label.includes("말"))) return true;
  if (labels.every((label) => ANNUAL_PERIOD_LABELS.has(label))) return null;
  return false;
};

/** 기말 시점 표/기간 표 기준에 대해 사용자에게 알릴 것의 종류. */
export enum PeriodEndNoticeKind {
  /** 양쪽 판정이 갈렸다 — 워크북에 맞는 선택지로 바꾸라고 한다. */
  MISMATCH = "MISMATCH",
  /** 워크북 쪽을 판정하지 못했다(null) — 틀렸다고 단정하지 않고 검토만 권한다. */
  UNKNOWN = "UNKNOWN",
}

export type PeriodEndNotice = {
  kind: PeriodEndNoticeKind;
  message: string;
};

/**
 * 같은 표의 DSD 워크북과 IXD 기말 시점 표/기간 표 기준을 사용자가 확인해야 하면 그 안내를,
 * 아니면 null. 무엇이 다른지는 워크북 칸 라벨(getWorkbookPeriodLabel)과 IXD 라벨을 나란히
 * 두어 보인다.
 *
 * - 갈리면: MISMATCH — 워크북에 맞는 비교표시유형으로 바꾸라고 한다.
 * - 백엔드가 워크북을 판정하지 못하면(null): UNKNOWN — 다르다고 단정하지 않고, 지금 IXD
 *   선택지가 맞는지 검토해 보라고만 한다.
 * - IXD를 모르면(null — 사업보고서, 비교표시유형이 저장되지 않은 표): 고를 선택지가 없거나
 *   맞출 기준이 없으니 알리지 않는다. 사업보고서는 편집기가 이 설정을 비활성화해 늘 null 이다.
 * - 워크북 표가 없으면(undefined — 아직 불러오지 않았거나 매칭된 표가 없음): 비교할 대상이
 *   없으니 알리지 않는다.
 */
export const getWorkbookIxdPeriodEndNotice = (
  workbookIsPeriodEnd: boolean | null | undefined,
  ixd: Pick<IxdDlView, "isPeriodEnd" | "periodLabels">,
): PeriodEndNotice | null => {
  if (workbookIsPeriodEnd === undefined) return null;
  // IXD 설정값(CompareType)이 우선이다. 필드가 없는 응답과 null 은 같은 뜻(모름)이다.
  const ixdIsPeriodEnd =
    ixd.isPeriodEnd ?? guessIxdIsPeriodEndFromLabels(ixd.periodLabels);
  if (ixdIsPeriodEnd === null || ixdIsPeriodEnd === workbookIsPeriodEnd) {
    return null;
  }
  if (workbookIsPeriodEnd === null) {
    return {
      kind: PeriodEndNoticeKind.UNKNOWN,
      message: `DSD 워크북 표의 기말 여부를 판단하지 못했습니다. 비교표시유형 '${COMPARE_DISPLAY_TYPE_BY_PERIOD_END[`${ixdIsPeriodEnd}`]}'이 맞는지 검토해주세요.`,
    };
  }
  return {
    kind: PeriodEndNoticeKind.MISMATCH,
    message: `비교표시유형을 '${COMPARE_DISPLAY_TYPE_BY_PERIOD_END[`${workbookIsPeriodEnd}`]}'로 바꿔주세요.`,
  };
};
